import { createApi } from '@reduxjs/toolkit/query/react';
import { Handover, HandoverDocByIdResponse } from '@handover/shared-types';
import { METHOD } from '../../constants/constant';
import { HANDOVER_END_POINTS } from '../../constants/constant';
import { customHandoverBaseQuery } from '../customBaseQuery';

const normalizeHandoverDocResponse = (response: any): HandoverDocByIdResponse => {
  const taskDoc = response?.handoverTaskDoc || response?.taskDoc || null;
  const normalizedTaskDoc = taskDoc
    ? {
        ...taskDoc,
        tasks: Array.isArray(taskDoc.tasks) ? taskDoc.tasks : [],
      }
    : null;

  return {
    ...response,
    handoverTaskDoc: normalizedTaskDoc,
    taskDoc: normalizedTaskDoc,
  };
};

export const handoverApi = createApi({
  reducerPath: 'handoverApi',
  baseQuery: customHandoverBaseQuery,
  tagTypes: ['Handover', 'HandoverList', 'HandoverArchive'],
  keepUnusedDataFor: 300, // Keep cached data for 5 minutes
  refetchOnMountOrArgChange: 30, // Refetch if data is older than 30 seconds
  refetchOnReconnect: true,
  refetchOnFocus: false,
  endpoints: builder => ({
    createHandover: builder.mutation<Handover, Partial<Handover>>({
      query: handover => ({
        url: HANDOVER_END_POINTS.createhandover,
        method: METHOD.POST,
        body: handover,
      }),
      invalidatesTags: ['HandoverList'],
    }),
    updateHandover: builder.mutation<Handover, { handoverId: string; data: Partial<Handover> }>({
      query: ({ handoverId, data }) => ({
        url: HANDOVER_END_POINTS.updatehandover.replace(':handoverId', handoverId),
        method: METHOD.PUT,
        body: data,
      }),
      invalidatesTags: (result, error, { handoverId }) => ['HandoverList', { type: 'Handover', id: handoverId }],
    }),
    getHandoverDocById: builder.query<HandoverDocByIdResponse, string>({
      query: handoverId => ({
        url: HANDOVER_END_POINTS.gethandoverdocbyid.replace(':handoverId', handoverId),
        method: METHOD.GET,
      }),
      transformResponse: normalizeHandoverDocResponse,
      providesTags: (result, error, handoverId) => [{ type: 'Handover', id: handoverId }],
      keepUnusedDataFor: 600, // Keep this specific query cached for 10 minutes
    }),
    getAllHandoversByCreatedByLoggedInUser: builder.query<Handover[], void>({
      query: () => ({
        url: HANDOVER_END_POINTS.getallhandoverbycreatedbyloggedinuser,
        method: METHOD.GET,
      }),
      transformResponse: (response: { handovers: Handover[] }) => response.handovers,
      providesTags: ['HandoverList'],
    }),
    getArchivedHandovers: builder.query<Handover[], void>({
      query: () => ({
        url: HANDOVER_END_POINTS.getarchivedhandovers,
        method: METHOD.GET,
      }),
      transformResponse: (response: { archivedHandovers: Handover[] }) =>
        response.archivedHandovers,
      providesTags: ['HandoverArchive'],
    }),
    deleteHandoverDocById: builder.mutation<{ message: string }, string>({
      query: handoverId => ({
        url: HANDOVER_END_POINTS.deletehandover.replace(':handoverId', handoverId),
        method: METHOD.DELETE,
      }),
      transformResponse: (response: { message: string }) => ({
        message: response.message,
      }),
      invalidatesTags: ['HandoverList', 'HandoverArchive'],
    }),
    restoreArchivedHandover: builder.mutation<
      { message: string; handover: Handover },
      { handoverId: string; restoreToUser: string }
    >({
      query: ({ handoverId, restoreToUser }) => ({
        url: HANDOVER_END_POINTS.restorearchivedhandover.replace(':handoverId', handoverId),
        method: METHOD.POST,
        body: { restoreToUser },
      }),
      invalidatesTags: ['HandoverList', 'HandoverArchive'],
    }),
  }),
});

export const {
  useCreateHandoverMutation,
  useUpdateHandoverMutation,
  useGetHandoverDocByIdQuery,
  useGetAllHandoversByCreatedByLoggedInUserQuery,
  useGetArchivedHandoversQuery,
  useDeleteHandoverDocByIdMutation,
  useRestoreArchivedHandoverMutation,
} = handoverApi;
